import { useContext, useMemo } from 'react';

import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import BonusPeriodsContext from 'contexts/BonusPeriodsContext';

import usePerformanceSlideOutFilterInput from './usePerformanceSlideOutFilterInput';
import useGridDocs from '../PerformanceBoardSlideOut/Sections/useGridDocs';

export interface BonusSubPeriodRow {
  key: string;
  label: string;
  doc: ElasticDocument | undefined;
}

/**
 * The selected driver's weekly bonus-dataset rows for the selected parent period,
 * one entry per sub-period (matched to its weekly doc by date). Shared by the
 * reward section header (for the period total) and the fact table (for the weekly
 * breakdown) so the data is fetched once per section. Empty for ordinary
 * (non-sub-period) programmes.
 */
const useBonusSubPeriodRows = (bonusConfigId: string) => {
  const { bonusPrograms } = useContext(DatasetDefinitionsContext);
  const { selectedBonusPeriod } = useContext(BonusPeriodsContext);

  const dataType = useMemo(
    () => bonusPrograms.find((p) => p.id === bonusConfigId)?.dataset,
    [bonusPrograms, bonusConfigId],
  );

  const subPeriods =
    selectedBonusPeriod && 'subPeriods' in selectedBonusPeriod
      ? selectedBonusPeriod.subPeriods
      : undefined;

  const filters = usePerformanceSlideOutFilterInput({
    datasetForDefaultFiltering: dataType,
  });

  const dateScope = useMemo((): DateRangeInput | undefined => {
    if (!selectedBonusPeriod) {
      return undefined;
    }
    return {
      startDate: selectedBonusPeriod.startDate,
      endDate: selectedBonusPeriod.endDate,
    };
  }, [selectedBonusPeriod]);

  const { docs, isLoading } = useGridDocs({
    dataType: dataType ?? '',
    filters: filters ?? { keywords: [] },
    dateScope: subPeriods?.length ? dateScope : undefined,
  });

  const rows = useMemo((): BonusSubPeriodRow[] => {
    if (!subPeriods) {
      return [];
    }
    return subPeriods.map((subPeriod) => ({
      key: subPeriod.startDate,
      label: subPeriod.label,
      doc: docs.find((doc) => {
        const date = doc['date'];
        return (
          typeof date === 'string' &&
          date >= subPeriod.startDate &&
          date <= subPeriod.endDate
        );
      }),
    }));
  }, [subPeriods, docs]);

  return { rows, dataType, isLoading };
};

export default useBonusSubPeriodRows;
